import { ENV } from '../lib/env';
// src/app/lib/api.ts e este arquivo escrevem/leem as mesmas chaves de
// localStorage (access_token/refresh_token). O backend usa
// ROTATE_REFRESH_TOKENS + BLACKLIST_AFTER_ROTATION, então dois refreshes
// concorrentes (um por cliente) invalidam um ao outro. Reaproveita o
// singleton de refresh do lib/api.ts em vez de manter um segundo aqui.
import { refreshAccessToken as refreshSharedAccessToken } from '../lib/api';

const BASE_URL = ENV.API_URL ?? 'http://localhost:8000/api/v1';

// ─── Token Storage ──────────────────────────────────────────────────

export const tokenStorage = {
  getAccess: (): string | null => localStorage.getItem('access_token'),
  getRefresh: (): string | null => localStorage.getItem('refresh_token'),
  setTokens: (access: string, refresh: string): void => {
    localStorage.setItem('access_token', access);
    localStorage.setItem('refresh_token', refresh);
  },
  clearTokens: (): void => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  },
};

// ─── Error Class ─────────────────────────────────────────────────────

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    /** Corpo da resposta de erro (ex.: erros por campo do DRF), quando veio JSON. */
    public readonly data?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ─── Error Message ─────────────────────────────────────────────────────────────

/** Primeira string encontrada num valor de erro do DRF (string, lista ou objeto aninhado). */
function firstString(value: unknown): string | undefined {
  if (typeof value === 'string') return value || undefined;
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = firstString(item);
      if (found) return found;
    }
    return undefined;
  }
  if (value && typeof value === 'object') {
    for (const item of Object.values(value)) {
      const found = firstString(item);
      if (found) return found;
    }
  }
  return undefined;
}

/**
 * Extrai uma mensagem legível do corpo de erro da API. Cobre os formatos
 * usados no backend: `detail`/`message`/`error` (views), `non_field_errors`
 * e erros por campo do DRF (ex.: `{file: ["File type not allowed..."]}` do
 * upload de storage). Retorna undefined se nada for aproveitável.
 */
export function extractErrorMessage(err: Record<string, unknown>): string | undefined {
  for (const key of ['detail', 'message', 'error', 'non_field_errors']) {
    const found = firstString(err[key]);
    if (found) return found;
  }
  return firstString(err);
}

// ─── Core Request ──────────────────────────────────────────────────────────────

interface RequestOptions {
  method?: string;
  body?: unknown;
  params?: Record<string, string>;
  headers?: Record<string, string>;
  skipAuth?: boolean;
}

async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, params, headers: extraHeaders = {}, skipAuth = false } = options;

  const url = new URL(`${BASE_URL}${path}`);
  if (params) {
    Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
  }

  const buildHeaders = (token: string | null): HeadersInit => {
    const h: Record<string, string> = body instanceof FormData ? {} : { 'Content-Type': 'application/json' };
    Object.assign(h, extraHeaders);
    if (token && !skipAuth) h['Authorization'] = `Bearer ${token}`;
    return h;
  };

  const makeRequest = async (token: string | null): Promise<Response> =>
    fetch(url.toString(), {
      method,
      headers: buildHeaders(token),
      body: body instanceof FormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
    });

  let response = await makeRequest(skipAuth ? null : tokenStorage.getAccess());

  // Auto-refresh on 401 — usa o singleton compartilhado do lib/api.ts
  if (response.status === 401 && !skipAuth) {
    const newToken = await refreshSharedAccessToken();
    if (!newToken) {
      tokenStorage.clearTokens();
      window.dispatchEvent(new Event(':logout'));
      throw new ApiError(401, 'Sessão expirada. Faça login novamente.');
    }
    response = await makeRequest(newToken);
  }

  if (!response.ok) {
    let message = response.status >= 500
      ? `Erro no servidor (${response.status}). Tente novamente em instantes.`
      : `Erro ${response.status}`;
    let data: Record<string, unknown> | undefined;
    try {
      const err = (await response.json()) as Record<string, unknown>;
      data = err;
      message = extractErrorMessage(err) ?? message;
    } catch {
      // corpo não-JSON (ex.: página HTML de 500) — fica a mensagem padrão
    }
    throw new ApiError(response.status, message, data);
  }

  // 204 No Content
  if (response.status === 204) return undefined as T;

  return response.json() as Promise<T>;
}

// ─── HTTP Helpers ──────────────────────────────────────────────────────────

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'GET' }),

  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'POST', body }),

  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'PATCH', body }),

  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'PUT', body }),

  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'DELETE' }),
};
